/**
 * NewsSheet — newspaper-style listing of recent Bitcoin news.
 * Opens from the news ticker banner. Supports pull-to-refresh.
 */

import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { NewsItem } from '@/hooks/useBuddyBackend';

interface NewsSheetProps {
  open: boolean;
  onClose: () => void;
  newsItems?: NewsItem[];
  onRefresh: () => Promise<void>;
}

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const PULL_THRESHOLD = 60;

export default function NewsSheet({ open, onClose, newsItems, onRefresh }: NewsSheetProps) {
  const [refreshing, setRefreshing] = useState(false);
  const [pullY, setPullY] = useState(0);
  const touchStartY = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pulling = useRef(false);

  const items = newsItems?.filter(n => Date.now() - n.pubDate < 24 * 60 * 60 * 1000) ?? [];

  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setTimeout(() => setRefreshing(false), 600);
    }
  }, [onRefresh, refreshing]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    if (scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      pulling.current = true;
    }
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!pulling.current) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (dy > 0) {
      setPullY(Math.min(dy * 0.4, 80));
    } else {
      pulling.current = false;
      setPullY(0);
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (pullY >= PULL_THRESHOLD) {
      handleRefresh();
    }
    pulling.current = false;
    setPullY(0);
  }, [pullY, handleRefresh]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-[90]"
            style={{ background: 'hsl(var(--background) / 0.7)', backdropFilter: 'blur(6px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Sheet — draggable to dismiss */}
          <motion.div
            className="fixed inset-x-0 bottom-0 z-[91] max-h-[85vh] flex flex-col"
            style={{
              background: 'hsl(var(--card))',
              borderTop: '1px solid hsl(var(--foreground) / 0.1)',
              borderRadius: '16px 16px 0 0',
              boxShadow: '0 -8px 40px hsl(var(--background) / 0.5)',
              touchAction: 'none',
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_e, info) => {
              if (info.offset.y > 100 || info.velocity.y > 300) {
                onClose();
              }
            }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full" style={{ background: 'hsl(var(--foreground) / 0.15)' }} />
            </div>

            {/* Header */}
            <div className="px-5 pb-3 pt-1 flex items-end justify-between">
              <div>
                <p className="text-[7px] font-body font-semibold tracking-[0.25em] uppercase" style={{ color: 'hsl(var(--bitcoin) / 0.7)' }}>
                  Bitcoin
                </p>
                <h2 className="text-[18px] font-display font-bold tracking-tight leading-tight" style={{ color: 'hsl(var(--foreground) / 0.9)' }}>
                  Latest News
                </h2>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all active:scale-95"
                style={{
                  background: 'hsl(var(--foreground) / 0.05)',
                  border: '1px solid hsl(var(--foreground) / 0.08)',
                  color: refreshing ? 'hsl(var(--bitcoin) / 0.8)' : 'hsl(var(--foreground) / 0.45)',
                }}
              >
                <motion.span
                  className="text-[11px]"
                  animate={refreshing ? { rotate: 360 } : {}}
                  transition={refreshing ? { duration: 0.8, repeat: Infinity, ease: 'linear' } : {}}
                >
                  ⟳
                </motion.span>
                <span className="text-[8px] font-body font-semibold tracking-wider uppercase">
                  {refreshing ? 'Updating…' : 'Refresh'}
                </span>
              </button>
            </div>

            <div className="mx-5 mb-1" style={{ height: 1, background: 'hsl(var(--foreground) / 0.08)' }} />

            {/* Pull-to-refresh indicator */}
            <AnimatePresence>
              {pullY > 10 && (
                <motion.div
                  className="flex items-center justify-center py-1"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: pullY > 0 ? Math.min(pullY, 40) : 0 }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <motion.span
                    className="text-[14px]"
                    animate={{ rotate: pullY >= PULL_THRESHOLD ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    ↓
                  </motion.span>
                  <span className="text-[8px] font-body ml-1.5" style={{ color: 'hsl(var(--foreground) / 0.4)' }}>
                    {pullY >= PULL_THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* News list */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-5 pb-6 space-y-0"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {/* Story count */}
              <p className="text-[9px] font-body py-2" style={{ color: 'hsl(var(--foreground) / 0.3)' }}>
                {items.length} {items.length === 1 ? 'story' : 'stories'} in the last 24 hours
              </p>

              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <span className="text-[24px]">📡</span>
                  <p className="text-[11px] font-body" style={{ color: 'hsl(var(--foreground) / 0.4)' }}>
                    No news in the last 24 hours
                  </p>
                  <button
                    onClick={handleRefresh}
                    className="text-[9px] font-body font-semibold mt-2 px-3 py-1.5 rounded-full"
                    style={{
                      color: 'hsl(var(--bitcoin))',
                      background: 'hsl(var(--bitcoin) / 0.1)',
                      border: '1px solid hsl(var(--bitcoin) / 0.2)',
                    }}
                  >
                    Check for news
                  </button>
                </div>
              ) : (
                items.map((item, i) => (
                  <motion.div
                    key={`${item.title}-${i}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.25 }}
                  >
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block py-3 group"
                        style={{ borderBottom: '1px solid hsl(var(--foreground) / 0.05)' }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-[13px] font-body font-medium leading-snug group-hover:underline"
                              style={{ color: 'hsl(var(--foreground) / 0.85)' }}
                            >
                              {item.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <span
                                className="text-[9px] font-body font-semibold tracking-wider uppercase"
                                style={{ color: 'hsl(var(--bitcoin) / 0.65)' }}
                              >
                                {item.source}
                              </span>
                              <span className="text-[8px]" style={{ color: 'hsl(var(--foreground) / 0.2)' }}>·</span>
                              <span
                                className="text-[9px] font-body tabular-nums"
                                style={{ color: 'hsl(var(--foreground) / 0.35)' }}
                              >
                                {timeAgo(item.pubDate)}
                              </span>
                            </div>
                          </div>
                          <span className="text-[10px] mt-1 shrink-0" style={{ color: 'hsl(var(--foreground) / 0.2)' }}>
                            ↗
                          </span>
                        </div>
                      </a>
                    ) : (
                      <div
                        className="py-3"
                        style={{ borderBottom: '1px solid hsl(var(--foreground) / 0.05)' }}
                      >
                        <p
                          className="text-[13px] font-body font-medium leading-snug"
                          style={{ color: 'hsl(var(--foreground) / 0.7)' }}
                        >
                          {item.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span
                            className="text-[9px] font-body font-semibold tracking-wider uppercase"
                            style={{ color: 'hsl(var(--bitcoin) / 0.65)' }}
                          >
                            {item.source}
                          </span>
                          <span className="text-[8px]" style={{ color: 'hsl(var(--foreground) / 0.2)' }}>·</span>
                          <span
                            className="text-[9px] font-body tabular-nums"
                            style={{ color: 'hsl(var(--foreground) / 0.35)' }}
                          >
                            {timeAgo(item.pubDate)}
                          </span>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
